/**
 * Temporal Parking Integration for Ramp + Stack
 *
 * Bridges the gap between domain data structures (Ramp, Stack, Tenant) and
 * the temporal parking algorithm (parkingAlgorithm, Spot, Zone, ParkingRequest).
 *
 * Similar pattern to RampOptimization/algo/placement/rampIntegration.ts but for
 * time-based scheduling instead of spatial placement.
 */

import { Aircraft, Ramp, ScheduledTenant, Stack, Tenant } from "../../../types";
import {
  getPositionForSpot,
  getPositionForSpotLegacy,
  getSpotForPositionLegacy,
  OrientationType,
} from "../../../utils/spots";
import {
  Shape,
  SpotMetadata,
} from "../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { PartialPlacementInfo } from "../AutoPark/utils/detectPartialPlacements";
import { makeEmptyMask } from "./bitmap";
import { markOccupied } from "./cascadeRepair";
import {
  attemptLastMinuteInsertion,
  InsertionResult,
} from "./lastMinuteInsertion";
import { getDecisionLog, parkingAlgorithm } from "./parkingAlgorithm";
import { findSpotForPosition } from "./positionMapping";
import { SortingStrategy } from "./sortingStrategies";
import { planSnapshotPlacement } from "./temporalPlanner";
import {
  LogEntry,
  ParkingRequest,
  Placement,
  SizeCategory,
  Spot,
  EgressOption as TemporalEgressOption,
  Zone,
} from "./types";

/**
 * Options for running temporal parking on a ramp
 */
export interface TemporalParkingOptions {
  /** Planning window start time (default: now) */
  startTime?: Date;
  /** Planning horizon in hours (default: 24) */
  horizonHours?: number;
  /** Safety buffer in minutes around aircraft dwell (default: 15) */
  bufferMinutes?: number;
  /** Maximum allowed aircraft moves/relocations (default: 0 = no moves) */
  plannedMoveCap?: number;
  /** Allow placements that trap other aircraft (default: false) */
  allowTrapping?: boolean;
  /** Weight for egress conflict penalty (default: 1.0) */
  egressWeight?: number;
  /** Weight for spot waste penalty (default: 1.0) */
  wasteWeight?: number;
  /** Weight for zone priority penalty (default: 1.0) */
  zonePriorityWeight?: number;
  /** Weight for min dwell time preference (default: 2.0) */
  min_dwell_weight?: number;
  /** Trip timing data: trip_id -> { startTime, endTime } */
  tripTimings?: Map<string, { startTime: Date; endTime: Date }>;
  /** Enable arrival staging workflow (default: false) */
  enableArrivalStaging?: boolean;
  /** Enable departure staging workflow (default: false) */
  enableDepartureStaging?: boolean;
  /** Time for arrival staging operations (default: 60 min) */
  arrivalStagingDwellMinutes?: number;
  /** Time for departure staging operations (default: 30 min) */
  departureStagingDwellMinutes?: number;
  /** Minimum dwell time to use staging (default: 120 min) */
  quickTurnThresholdMinutes?: number;
}

/**
 * Result from temporal parking operation
 */
export interface TemporalPlacementResult {
  /** Raw placement result from algorithm */
  placement: Placement;
  /** Algorithm decision log for explainability */
  decisionLog: LogEntry[];
  /** Stack with updated tenant positions */
  updatedStack: Stack;
  /** Placement statistics */
  statistics: {
    totalAircraft: number;
    placedAircraft: number;
    unplacedAircraft: number;
    partiallyPlacedAircraft?: number;
    averageMoves: number;
    elapsedMs?: number;
  };
  /** Optional temporal data for timeline visualization */
  temporalData?: {
    spots: Spot[];
    requests: ParkingRequest[];
    zones: Zone[];
    horizonHours: number;
    startTime: Date;
    partialPlacements?: PartialPlacementInfo[];
  };
}

/**
 * Options for running snapshot parking on a ramp
 * Snapshot mode = all aircraft exist at same moment (no temporal conflicts)
 */
export interface SnapshotParkingOptions {
  /** Allow placements that trap other aircraft (default: false) */
  allowTrapping?: boolean;
  /** Weight for egress conflict penalty (default: 1.0) */
  egressWeight?: number;
  /** Weight for spot waste penalty (default: 1.0) */
  wasteWeight?: number;
  /** Weight for structural complexity penalty (default: 0.1) */
  structuralWeight?: number;
  /** Sorting strategy for request ordering (default: "size-constrained") */
  sortingStrategy?: SortingStrategy;
  /** Weight for zone priority penalty (default: 1.0) */
  zonePriorityWeight?: number;
}

/**
 * Result from snapshot parking operation
 */
export interface SnapshotPlacementResult {
  /** Raw placement result from algorithm */
  placement: Placement;
  /** Algorithm decision log for explainability */
  decisionLog: LogEntry[];
  /** Stack with updated tenant positions */
  updatedStack: Stack;
  /** Placement statistics */
  statistics: {
    totalAircraft: number;
    placedAircraft: number;
    unplacedAircraft: number;
    elapsedMs?: number;
  };
}

/**
 * Convert aircraft dimensions to size category
 * Uses maximum dimension (wingspan or length) since spots are rectangular
 * Both dimensions must fit within the spot threshold
 */
export function aircraftToSizeCategory(aircraft: Aircraft): SizeCategory {
  const maxDimension = Math.max(aircraft.wingspan, aircraft.length);

  if (maxDimension <= 40) return "very-small"; // Cessna 172, Piper PA-28
  if (maxDimension <= 60) return "small"; // Citations, Phenom 300
  if (maxDimension <= 80) return "midsize"; // Challenger 604/605
  if (maxDimension <= 100) return "super-midsize"; // Falcon 7X, G650
  if (maxDimension <= 120) return "large"; // Global 6000/7500, BBJ
  return "enormous"; // Aircraft too large for standard spots
}

/**
 * Convert ISO date string to time bucket index
 *
 * @param date - ISO date string (e.g., "2024-01-15T08:00:00Z")
 * @param startTime - Planning window start
 * @returns Bucket index (0 = startTime, 4 buckets per hour)
 */
export function dateToTimeBucket(date: string, startTime: Date): number {
  const targetDate = new Date(date);
  const minutesSinceStart =
    (targetDate.getTime() - startTime.getTime()) / (1000 * 60);
  return Math.floor(minutesSinceStart / 15); // 15-minute buckets
}

/**
 * Convert time bucket to ISO date string
 *
 * @param bucket - Bucket index
 * @param startTime - Planning window start
 * @returns ISO date string
 */
export function timeBucketToDate(bucket: number, startTime: Date): Date {
  const minutesOffset = bucket * 15;
  return new Date(startTime.getTime() + minutesOffset * 60 * 1000);
}

/**
 * Extract orientations from spot reference points
 * Maps reference point validOrientations to unique orientation IDs
 */
function extractOrientationsFromReferencePoints(shape: Shape) {
  const orientationSet = new Set<string>();

  // Extract all unique orientations from all reference points
  if (shape.reference_points) {
    for (const refPoint of shape.reference_points) {
      for (const orientation of refPoint.validOrientations) {
        orientationSet.add(orientation);
      }
    }
  }

  // Convert to Orientation objects
  if (orientationSet.size > 0) {
    return Array.from(orientationSet).map((id) => ({ id }));
  }

  // Fallback to center if no reference points
  return [{ id: "center" }];
}

/**
 * Extract spots from ramp shapes
 * Filters for shapes with type="Spot" and converts metadata
 */
export function buildSpotsFromRamp(ramp: Ramp, horizonHours: number): Spot[] {
  const totalBuckets = horizonHours * 4; // 15-minute buckets

  // Build map of zoneId → is_staging flag for propagation to spots
  const zoneMetadataMap = new Map<string, { is_staging?: boolean }>();
  ramp.shapes
    .filter((shape) => shape.type === "Zone")
    .forEach((zoneShape) => {
      const zoneMetadata = zoneShape.metadata as any;
      zoneMetadataMap.set(zoneShape.id, {
        is_staging: zoneMetadata?.is_staging || false,
      });
    });

  const spots = ramp.shapes
    .filter((shape) => shape.type === "Spot")
    .map((shape) => {
      const metadata = shape.metadata as SpotMetadata | undefined;

      // Propagate zone's is_staging flag to spot metadata
      const zoneId = metadata?.zoneId || "default";
      const zoneMetadata = zoneMetadataMap.get(zoneId);
      const isStaging = zoneMetadata?.is_staging || false;

      // Convert egress options from shape metadata
      const egressOptions: TemporalEgressOption[] | undefined =
        metadata?.egressOptions?.map((opt) => ({
          laneId: opt.laneId,
          laneName: opt.laneName,
          blocksSpotIds: opt.blocksSpotIds,
        }));

      // Extract orientations from reference_points
      const orientations = extractOrientationsFromReferencePoints(shape);

      // Merge zone's staging flag into spot's shape metadata
      const enrichedShape = {
        ...shape,
        metadata: {
          ...shape.metadata,
          is_staging: isStaging, // Propagate zone's is_staging to spot
        },
      };

      return {
        id: shape.id,
        zoneId: metadata?.zoneId || "default",
        allowedSizes: metadata?.allowedSizes || [],
        occupancy: makeEmptyMask(totalBuckets),
        orientations,
        egressOptions,
        max_dwell_minutes: metadata?.max_dwell_minutes, // Extract max duration limit from metadata
        min_dwell_minutes: metadata?.min_dwell_minutes, // Extract min duration requirement from metadata
        disabled: metadata?.disabled, // Extract disabled flag from metadata
        shape: enrichedShape, // REQUIRED - Include enriched Shape object with staging flag
      };
    });

  // Log sample of spots with their zone references and duration limits
  console.log(
    `[buildSpotsFromRamp] Built ${spots.length} spots, sample zoneIds:`,
    spots.slice(0, 3).map((s) => ({ spotId: s.id, zoneId: s.zoneId }))
  );

  // Log spots with duration limits
  const spotsWithLimits = spots.filter((s) => s.max_dwell_minutes);
  if (spotsWithLimits.length > 0) {
    console.log(
      `[buildSpotsFromRamp] Found ${spotsWithLimits.length} spots with duration limits:`,
      spotsWithLimits
        .slice(0, 5)
        .map((s) => ({ spotId: s.id, maxDwell: s.max_dwell_minutes }))
    );
  }

  // Log spots with min dwell requirements
  const spotsWithMinDwell = spots.filter((s) => s.min_dwell_minutes);
  if (spotsWithMinDwell.length > 0) {
    console.log(
      `[buildSpotsFromRamp] Found ${spotsWithMinDwell.length} spots with min dwell requirements:`,
      spotsWithMinDwell
        .slice(0, 5)
        .map((s) => ({ spotId: s.id, minDwell: s.min_dwell_minutes }))
    );
  }

  // Log disabled spots
  const disabledSpots = spots.filter((s) => s.disabled);
  if (disabledSpots.length > 0) {
    console.log(
      `[buildSpotsFromRamp] Found ${disabledSpots.length} disabled spots:`,
      disabledSpots.slice(0, 5).map((s) => s.id)
    );
  }

  // Log staging spots (propagated from zone metadata)
  const stagingSpots = spots.filter((s) => s.shape?.metadata?.is_staging);
  if (stagingSpots.length > 0) {
    console.log(
      `[buildSpotsFromRamp] Found ${stagingSpots.length} staging spots:`,
      stagingSpots.slice(0, 5).map((s) => ({ spotId: s.id, zoneId: s.zoneId }))
    );
  }

  return spots;
}

/**
 * Extract zones from ramp shapes
 * Filters for shapes with type="Zone" and builds zone structure
 */
export function buildZonesFromRamp(ramp: Ramp): Zone[] {
  const zones: Zone[] = [];

  // Find all zone shapes
  const zoneShapes = ramp.shapes.filter((shape) => shape.type === "Zone");

  console.log(`[buildZonesFromRamp] Found ${zoneShapes.length} zone shapes`);

  for (const zoneShape of zoneShapes) {
    // Find all spots belonging to this zone
    const spotIds = ramp.shapes
      .filter((shape) => {
        if (shape.type !== "Spot") return false;
        const metadata = shape.metadata as SpotMetadata | undefined;
        return metadata?.zoneId === zoneShape.id;
      })
      .map((shape) => shape.id);

    // Extract priority from shape.priority column (single source of truth)
    const priority = zoneShape.priority ?? 5;

    // Extract disabled flag from metadata
    const disabled = (zoneShape.metadata as any)?.disabled;

    // Extract allowOverflowParking flag from metadata (default: false)
    // When true, staging zones can be used for regular parking with penalty
    const allowOverflowParking =
      (zoneShape.metadata as any)?.allow_overflow_parking === true;

    zones.push({
      id: zoneShape.id,
      name: zoneShape.text || `Zone ${zoneShape.id}`,
      spotIds,
      priority,
      shapeId: zoneShape.id,
      disabled,
      allowOverflowParking,
    });
  }

  // Add default zone for spots without explicit zone
  const spotsWithoutZone = ramp.shapes
    .filter((shape) => {
      if (shape.type !== "Spot") return false;
      const metadata = shape.metadata as SpotMetadata | undefined;
      return !metadata?.zoneId || metadata.zoneId === "default";
    })
    .map((shape) => shape.id);

  if (spotsWithoutZone.length > 0) {
    zones.push({
      id: "default",
      name: "Default Zone",
      spotIds: spotsWithoutZone,
    });
  }

  // Cross-reference check: Find spots that reference non-existent zones
  const zoneIds = new Set(zones.map((z) => z.id));
  const allSpots = ramp.shapes.filter((s) => s.type === "Spot");
  const orphanedSpots = allSpots.filter((spot) => {
    const metadata = spot.metadata as SpotMetadata | undefined;
    const spotZoneId = metadata?.zoneId;
    return spotZoneId && spotZoneId !== "default" && !zoneIds.has(spotZoneId);
  });

  console.log(`[buildZonesFromRamp] Zone IDs extracted:`, Array.from(zoneIds));

  if (orphanedSpots.length > 0) {
    console.warn(
      `[buildZonesFromRamp] ⚠️ WARNING: ${orphanedSpots.length} spots reference non-existent zones!`
    );
    orphanedSpots.slice(0, 5).forEach((spot) => {
      const metadata = spot.metadata as SpotMetadata | undefined;
      console.warn(
        `  - Spot ${spot.text || spot.id} references zone "${
          metadata?.zoneId
        }" which doesn't exist`
      );
    });
    if (orphanedSpots.length > 5) {
      console.warn(
        `  ... and ${
          orphanedSpots.length - 5
        } more spots with orphaned zone references`
      );
    }
  }

  // Log disabled zones
  const disabledZones = zones.filter((z) => z.disabled);
  if (disabledZones.length > 0) {
    console.log(
      `[buildZonesFromRamp] Found ${disabledZones.length} disabled zones:`,
      disabledZones.map((z) => ({
        id: z.id,
        name: z.name,
        spotCount: z.spotIds.length,
      }))
    );
  }

  return zones;
}

/**
 * Convert stack tenants with trips to parking requests
 * Uses real trip timing data when provided via tripTimings map
 */
function buildParkingRequests(
  stack: Stack,
  startTime: Date,
  _horizonHours: number,
  tripTimings: Map<string, { startTime: Date; endTime: Date }>
): ParkingRequest[] {
  return stack.tenants
    .filter((tenant) => tenant.trip_id) // Only tenants with scheduled trips
    .map((tenant) => {
      // Get real trip timings from map
      const timing = tripTimings.get(tenant.trip_id!);

      if (!timing) {
        console.warn(
          `[buildParkingRequests] No timing data for trip ${tenant.trip_id}, skipping`
        );
        return null;
      }

      // Convert real times to time buckets
      const arrivalBucket = dateToTimeBucket(
        timing.startTime.toISOString(),
        startTime
      );
      const departureBucket = dateToTimeBucket(
        timing.endTime.toISOString(),
        startTime
      );

      return {
        id: tenant.id,
        tail_number: tenant.tail_number,
        sizeCategory: aircraftToSizeCategory(tenant.aircraft),
        arrivalBucket,
        departureBucket,
        aircraft: tenant.aircraft,
      };
    })
    .filter((r) => r !== null) as ParkingRequest[]; // Filter out nulls
}

/**
 * Apply temporal placement results back to stack
 * Updates tenant positions based on algorithm placement
 */
function applyTemporalPlacementsToStack(
  stack: Stack,
  placement: Placement,
  spots: Spot[],
  rampShapes: Shape[],
  options?: any
): Stack {
  if (!options.rampDepth) throw new Error("rampDepth is required.");

  const updatedTenants = stack.tenants.map((tenant) => {
    const tenantPlacement = placement[tenant.id];
    if (!tenantPlacement) {
      // Not placed - leave position unchanged
      return tenant;
    }

    // Extract spotId and orientationId from placement
    let spotId: string;
    let orientationId: string;
    if ("spotId" in tenantPlacement) {
      spotId = tenantPlacement.spotId;
      orientationId = tenantPlacement.orientationId;
    } else {
      spotId = tenantPlacement.segments[0].spotId;
      orientationId = tenantPlacement.segments[0].orientationId;
    }

    const spot = spots.find((s) => s.id === spotId);
    // Find spot shape (has reference_points and geometry)
    if (!spot) {
      console.log(`Failed to find spot with id ${spotId}`);
      return tenant;
    }

    // Get position using orientationId from placement
    console.log(
      `positioning tenant ${tenant.tail_number} at spot ${spotId}`,
      spot
    );

    const position = getPositionForSpot(
      spot,
      rampShapes,
      orientationId as OrientationType,
      tenant,
      {
        checkAircraftFitsInBox: false,
        //feetToPixels: options?.feetToPixels,
        yAxisInverted: true,
        //rampDepth: options?.rampDepth,
      }
    );

    if (!position) {
      console.error(
        `Failed to get position for ${tenant.tail_number} at spot ${spotId} with orientation ${orientationId}`
      );
      return tenant; // Keep original position - placement is invalid
    }

    return {
      ...tenant,
      position: {
        ...tenant.position,
        x: position.x,
        // We have passed in to the getPositionForSpot function a coordinate system where
        // the origin is top left, but we need to return a coordinate system
        // where the origin is bottom left for this positioning, so we modify it
        // here
        // TODO It doesn't make sense that we're passing in top left origin coords
        // and returning something different, there must be a conversion going
        // on somewhere else that we can remove and then remove this.
        y: options?.rampDepth - position.y,
        angle: position.angle,
        timestamp: Date.now(),
      },
    };
  });

  return {
    ...stack,
    tenants: updatedTenants,
  };
}

/**
 * Main entry point: Run temporal parking algorithm on Ramp + Stack
 *
 * @param ramp - Ramp with spot/zone shapes
 * @param stack - Stack with scheduled tenants
 * @param options - Algorithm options
 * @returns Placement results with updated stack
 */
export function runTemporalParkingOnRamp(
  ramp: Ramp,
  stack: Stack,
  options: TemporalParkingOptions = {}
): TemporalPlacementResult {
  const {
    startTime = new Date(),
    horizonHours = 24,
    bufferMinutes = 15,
    plannedMoveCap = 0,
    allowTrapping = false,
    egressWeight = 1.0,
    wasteWeight = 1.0,
    zonePriorityWeight = 1.0,
    min_dwell_weight = 2.0,
    tripTimings = new Map(),
    enableArrivalStaging = false,
    enableDepartureStaging = false,
    arrivalStagingDwellMinutes = 60,
    departureStagingDwellMinutes = 30,
    quickTurnThresholdMinutes = 120,
  } = options;

  console.log(
    `[Temporal Parking] Running on ramp "${ramp.name}" with ${stack.tenants.length} aircraft`
  );

  // Convert ramp data to algorithm format
  const spots = buildSpotsFromRamp(ramp, horizonHours);
  const zones = buildZonesFromRamp(ramp);
  const requests = buildParkingRequests(
    stack,
    startTime,
    horizonHours,
    tripTimings
  );

  console.log(
    `[Temporal Parking] Found ${spots.length} spots, ${zones.length} zones, ${requests.length} requests`
  );

  // Validate we have spots
  if (spots.length === 0) {
    console.warn(
      "[Temporal Parking] No spots found in ramp. Add shapes with type='Spot'"
    );
    return {
      placement: {},
      decisionLog: [],
      updatedStack: stack,
      statistics: {
        totalAircraft: requests.length,
        placedAircraft: 0,
        unplacedAircraft: requests.length,
        averageMoves: 0,
      },
    };
  }

  // Run temporal parking algorithm
  const placement = parkingAlgorithm(
    requests,
    spots,
    bufferMinutes,
    plannedMoveCap,
    allowTrapping,
    egressWeight,
    wasteWeight,
    0.1, // structuralWeight (fixed for temporal)
    "arrival", // sorting strategy
    horizonHours,
    // shapes parameter removed - using spot.shape
    false, // useMaskOptimization
    false, // validateMasks
    zonePriorityWeight,
    zones,
    min_dwell_weight,
    0.0, // stickinessWeight (not used in temporal mode)
    enableArrivalStaging,
    enableDepartureStaging,
    arrivalStagingDwellMinutes,
    departureStagingDwellMinutes,
    quickTurnThresholdMinutes
  );

  const decisionLog = getDecisionLog();

  // Apply results back to stack
  const updatedStack = applyTemporalPlacementsToStack(
    stack,
    placement,
    spots,
    ramp.shapes,
    { rampDepth: ramp.depth }
  );

  // Calculate statistics
  const placedCount = Object.keys(placement).length;
  const unplacedCount = requests.length - placedCount;

  // Calculate average moves
  let totalMoves = 0;
  let placementsWithSegments = 0;
  Object.values(placement).forEach((p) => {
    if ("segments" in p && "moveCount" in p) {
      totalMoves += p.moveCount;
      placementsWithSegments++;
    }
  });
  const averageMoves =
    placementsWithSegments > 0 ? totalMoves / placementsWithSegments : 0;

  console.log(
    `[Temporal Parking] Placed ${placedCount}/${
      requests.length
    }, avg moves: ${averageMoves.toFixed(2)}`
  );

  // Log decision log for debugging
  if (decisionLog.length > 0) {
    console.log(
      "[Temporal Parking] Decision Log:\n" +
        JSON.stringify(decisionLog, null, 2)
    );
  }

  return {
    placement,
    decisionLog,
    updatedStack,
    statistics: {
      totalAircraft: requests.length,
      placedAircraft: placedCount,
      unplacedAircraft: unplacedCount,
      averageMoves,
    },
  };
}

/**
 * Build snapshot parking requests (no temporal data needed)
 * All aircraft exist at bucket 0 (single moment in time)
 *
 * @param stack - Stack with tenants to place
 * @returns Parking requests configured for snapshot mode
 */
function buildSnapshotRequests(stack: Stack): ParkingRequest[] {
  return stack.tenants.map((tenant) => ({
    id: tenant.id,
    tail_number: tenant.tail_number,
    sizeCategory: aircraftToSizeCategory(tenant.aircraft),
    arrivalBucket: 0, // Snapshot mode: all at bucket 0
    departureBucket: 1, // Snapshot mode: all at bucket 1
    aircraft: tenant.aircraft,
    placement_source: tenant.placement_source || "auto",
    is_locked: tenant.is_locked || false,
  }));
}

/**
 * Run snapshot parking on current ramp state
 *
 * Snapshot mode places all aircraft as if they exist at the same moment in time.
 * This eliminates temporal conflicts - useful for current ramp layout optimization.
 *
 * Key differences from temporal parking:
 * - No arrival/departure times needed
 * - No time-based conflicts
 * - No aircraft moves/segmentation
 * - Simpler and faster
 *
 * @param ramp - Ramp with spot/zone shapes
 * @param stack - Stack with tenants to place
 * @param options - Snapshot parking options
 * @returns Placement results with updated stack
 */
export function runSnapshotParkingOnRamp(
  ramp: Ramp,
  stack: Stack,
  options: SnapshotParkingOptions = {}
): SnapshotPlacementResult {
  const {
    allowTrapping = false,
    egressWeight = 1.0,
    wasteWeight = 1.0,
    structuralWeight = 0.1,
    sortingStrategy = "size-constrained",
    zonePriorityWeight = 1.0,
  } = options;

  console.log(
    `[Snapshot Parking] Running on ramp "${ramp.name}" with ${stack.tenants.length} aircraft`
  );

  // Convert ramp data to algorithm format (reuse temporal functions)
  const spots = buildSpotsFromRamp(ramp, 0.25); // 1 bucket for snapshot
  const zones = buildZonesFromRamp(ramp);
  const requests = buildSnapshotRequests(stack);

  console.log(
    `[Snapshot Parking] Found ${spots.length} spots, ${zones.length} zones, ${requests.length} requests`
  );

  // Validate we have spots
  if (spots.length === 0) {
    console.warn(
      "[Snapshot Parking] No spots found in ramp. Add shapes with type='Spot'"
    );
    return {
      placement: {},
      decisionLog: [],
      updatedStack: stack,
      statistics: {
        totalAircraft: requests.length,
        placedAircraft: 0,
        unplacedAircraft: requests.length,
      },
    };
  }

  // Run snapshot placement algorithm
  const result = planSnapshotPlacement(
    requests,
    spots,
    allowTrapping,
    egressWeight,
    wasteWeight,
    structuralWeight,
    sortingStrategy,
    zonePriorityWeight,
    zones
  );

  const decisionLog = getDecisionLog();

  // Apply results back to stack (reuse temporal function)
  const updatedStack = applyTemporalPlacementsToStack(
    stack,
    result.placement,
    spots,
    ramp.shapes,
    {
      rampDepth: ramp.depth,
      // Only used for testing
      //feetToPixels: document.getElementById('aircraftDebugDrawLayer').clientWidth / ramp.width,
    }
  );

  // Calculate statistics
  const placedCount = Object.keys(result.placement).length;
  const unplacedCount = requests.length - placedCount;

  // Log decision log for debugging
  if (decisionLog.length > 0) {
    console.log(
      "[Snapshot Parking] Decision Log:\n" +
        JSON.stringify(decisionLog, null, 2)
    );
  }

  return {
    placement: result.placement,
    decisionLog,
    updatedStack,
    statistics: {
      totalAircraft: requests.length,
      placedAircraft: placedCount,
      unplacedAircraft: unplacedCount,
    },
  };
}

/**
 * Run incremental parking on current ramp state for a single selected aircraft
 *
 * This places ONLY the selected aircraft while treating all other placed aircraft
 * as locked/immovable. Existing placements are converted to occupied spot bitmaps.
 *
 * Use cases:
 * - Add new aircraft after AutoPark has run
 * - Re-place specific aircraft without disturbing others
 * - Iteratively build ramp layout one aircraft at a time
 *
 * @param ramp - Ramp with spot/zone shapes
 * @param stack - Stack with tenants (one should have .selected = true)
 * @param selectedAircraftId - ID of aircraft to place
 * @param options - Snapshot parking options
 * @returns Placement result with only selected aircraft updated
 *
 * @example
 * ```typescript
 * const selectedAircraft = stack.tenants.find(t => t.selected);
 * const result = runIncrementalParkingOnRamp(ramp, stack, selectedAircraft.id, {
 *   allowTrapping: false,
 *   egressWeight: 1.0,
 *   wasteWeight: 1.0,
 * });
 * setStack(result.updatedStack);
 * ```
 */
export function runIncrementalParkingOnRamp(
  ramp: Ramp,
  stack: Stack,
  selectedAircraftId: string,
  options: SnapshotParkingOptions = {}
): SnapshotPlacementResult {
  // 1. Find selected aircraft
  const selectedAircraft = stack.tenants.find(
    (t) => t.id === selectedAircraftId
  );
  if (!selectedAircraft) {
    throw new Error(
      `Selected aircraft ${selectedAircraftId} not found in stack`
    );
  }

  // 2. Get existing placements (all others with positions set)
  const existingPlacements = stack.tenants.filter(
    (t) =>
      t.id !== selectedAircraftId &&
      t.position.x !== null &&
      t.position.y !== null
  );

  // 3. Build spots from ramp (snapshot mode: 1 bucket)
  const spots = buildSpotsFromRamp(ramp, 0.25);
  const zones = buildZonesFromRamp(ramp);

  // Validate we have spots
  if (spots.length === 0) {
    console.warn(
      "[Incremental Parking] No spots found in ramp. Add shapes with type='Spot'"
    );
    return {
      placement: {},
      decisionLog: [],
      updatedStack: stack,
      statistics: {
        totalAircraft: 1,
        placedAircraft: 0,
        unplacedAircraft: 1,
      },
    };
  }

  // 4. Mark existing placements as occupied in spot bitmaps
  // Build a map of legacy spots to their Spot index (for stable matching)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const legacySpotToIndex = new Map<any, number>();

  const legacySpots = spots.map((spot, index) => {
    const legacySpot = {
      points: spot.shape.points,
      referencePoints: spot.shape.reference_points || [],
    };
    legacySpotToIndex.set(legacySpot, index);
    return legacySpot;
  });

  // Mark each existing placement's spot as occupied
  for (const tenant of existingPlacements) {
    // Apply Y-flip transformation: database positions use top-left origin,
    // spot shapes use bottom-left origin (same as calculateSpotForPlacement in ScheduledRampView)
    const transformedPosition = {
      x: tenant.position.x,
      y: ramp.depth - tenant.position.y!,
      angle: tenant.position.angle,
    };

    const result = getSpotForPositionLegacy(
      tenant,
      transformedPosition,
      legacySpots,
      {}
    );

    if (result) {
      // Use the map for stable spot matching (not reference equality)
      const spotIndex = legacySpotToIndex.get(result.spot);

      if (spotIndex !== undefined) {
        // Mark entire spot as occupied for all time buckets
        // Fill all 64-bit words with 1s (fully occupied)
        spots[spotIndex].occupancy.fill(BigInt("0xFFFFFFFFFFFFFFFF"));
      } else {
        console.warn(
          `[Incremental Parking] Could not find spot index for tenant ${tenant.tail_number}`
        );
      }
    }
  }

  // 5. Create parking request for selected aircraft only
  const request: ParkingRequest = {
    id: selectedAircraft.id,
    tail_number: selectedAircraft.tail_number,
    sizeCategory: aircraftToSizeCategory(selectedAircraft.aircraft),
    arrivalBucket: 0, // Snapshot mode
    departureBucket: 1, // Snapshot mode
    aircraft: selectedAircraft.aircraft,
    placement_source: selectedAircraft.placement_source || "auto",
    is_locked: selectedAircraft.is_locked || false,
  };

  // 6. Run snapshot placement for single aircraft
  const result = planSnapshotPlacement(
    [request], // Single aircraft
    spots,
    options.allowTrapping ?? false,
    options.egressWeight ?? 1.0,
    options.wasteWeight ?? 1.0,
    options.structuralWeight ?? 0.1,
    options.sortingStrategy ?? "size-constrained",
    options.zonePriorityWeight ?? 1.0,
    zones
  );

  // 7. Get decision log
  const decisionLog = getDecisionLog();

  // 8. Apply placement to stack (only updates selected aircraft)
  const updatedStack = applyTemporalPlacementsToStack(
    stack,
    result.placement,
    spots,
    ramp.shapes,
    { rampDepth: ramp.depth }
  );

  // 9. Return result
  const placedCount = Object.keys(result.placement).length;

  return {
    placement: result.placement,
    decisionLog,
    updatedStack,
    statistics: {
      totalAircraft: 1,
      placedAircraft: placedCount,
      unplacedAircraft: placedCount === 0 ? 1 : 0,
    },
  };
}

/**
 * Convert temporal placement results to database position segments
 *
 * This is the INVERSE of applyTemporalPlacementsToStack:
 * - applyTemporalPlacementsToStack: spot IDs → stack positions (for visual display)
 * - convertPlacementToPositionSegments: spot IDs → DB segments (for persistence)
 *
 * **Coordinate System:** Uses Y-flipped shapes (like snapshot mode) for database compatibility.
 * - Visual display (Timeline): Uses non-flipped coordinates (temporal mode)
 * - Database storage: Uses Y-flipped coordinates (snapshot mode system)
 * - This ensures database positions render correctly when reloaded
 *
 * @param placement - Algorithm output (spot IDs + orientations)
 * @param rampShapes - Ramp shapes (for geometry lookup)
 * @param ramp - Full ramp object (for depth, needed by getPositionForSpot and Y-flip)
 * @param requestMap - Map of placementId → ParkingRequest (contains aircraft and tail_number)
 * @param startTime - Time bucket 0 reference point
 * @param horizonHours - Planning window duration
 * @returns Array of {placementId, segments: [{startTime, endTime, x, y, angle}]}
 *
 * @example
 * const results = convertPlacementToPositionSegments(
 *   lastResult.placement,
 *   ramp.shapes,
 *   ramp,
 *   requestMap,
 *   timeRange.start,
 *   24
 * );
 * // results[0] = { placementId: "p123", segments: [{startTime, endTime, x, y, angle}] }
 */
export function convertPlacementToPositionSegments(
  placement: Placement,
  rampShapes: Shape[],
  ramp: { id: string; depth: number },
  requestMap: Map<string, ParkingRequest>,
  startTime: Date,
  _horizonHours: number
): Array<{
  placementId: string;
  segments: Array<{
    startTime: Date;
    endTime: Date;
    x: number;
    y: number;
    angle: number;
  }>;
}> {
  const results: Array<{
    placementId: string;
    segments: Array<{
      startTime: Date;
      endTime: Date;
      x: number;
      y: number;
      angle: number;
    }>;
  }> = [];

  // Process each placed aircraft
  for (const [placementId, placementData] of Object.entries(placement)) {
    // Get request data (contains both aircraft and tail_number)
    const request = requestMap.get(placementId);
    if (!request) {
      console.warn(
        `[convertPlacementToPositionSegments] No request found for placement ${placementId}, skipping`
      );
      continue;
    }

    const aircraft = request.aircraft;

    // Build list of time segments with spot IDs
    const timeSegments: Array<{
      spotId: string;
      orientationId: string;
      startBucket: number;
      endBucket: number;
    }> = [];

    if ("spotId" in placementData) {
      // Single-spot placement (no moves)
      // Full dwell time = trip arrival to departure (not entire horizon)
      timeSegments.push({
        spotId: placementData.spotId,
        orientationId: placementData.orientationId,
        startBucket: request.arrivalBucket,
        endBucket: request.departureBucket,
      });
    } else {
      // Multi-segment placement (with moves)
      for (const seg of placementData.segments) {
        timeSegments.push({
          spotId: seg.spotId,
          orientationId: seg.orientationId,
          startBucket: seg.startBucket,
          endBucket: seg.endBucket,
        });
      }
    }

    // Convert each time segment to position coordinates
    // Use flatMap to filter out failed segments (returns [] for failures)
    const positionSegments = timeSegments.flatMap((seg) => {
      // Find spot shape for geometry
      var spotShape = rampShapes.find(
        (s) => s.id === seg.spotId && s.type === "Spot"
      );

      if (!spotShape) {
        console.error(
          `[convertPlacementToPositionSegments] Spot shape not found:`,
          { spotId: seg.spotId, placementId }
        );
        return []; // Skip this segment
      }

      // The current spot position has an origin of the top left of the screen
      // so we need to invert it in the y axis and reverse the corners. This
      // ensures it matches the coordinate system of everything else
      spotShape = Object.assign({}, spotShape);
      spotShape.points = spotShape.points.map((o) => ({
        x: o.x,
        y: ramp.depth - o.y,
      }));
      spotShape.points.reverse();

      // Build spot object for getPositionForSpot
      const spotUtil = {
        points: spotShape.points,
        referencePoints: spotShape.reference_points || [],
      };

      // Create minimal tenant object for getPositionForSpot
      // (getPositionForSpot needs aircraft dimensions)
      const minimalTenant = {
        aircraft,
      };

      // Calculate position for database storage
      // NOTE: Unlike visual display (Timeline), database uses snapshot mode coordinates
      const position = getPositionForSpotLegacy(
        spotUtil,
        seg.orientationId as OrientationType,
        minimalTenant as any,
        {
          checkAircraftFitsInBox: false,
        }
      );

      if (!position) {
        // Log detailed info to help diagnose the issue
        console.error(
          `[convertPlacementToPositionSegments] Position calculation failed:`,
          {
            spotId: seg.spotId,
            orientationId: seg.orientationId,
            placementId,
            referencePointsCount: spotUtil.referencePoints.length,
            referencePoints: spotUtil.referencePoints.map((rp) => ({
              name: rp.name,
              validOrientations: rp.validOrientations,
            })),
          }
        );
        return []; // Skip this segment - partial success is better than total failure
      }

      // Convert time buckets to Date objects
      const segmentStart = timeBucketToDate(seg.startBucket, startTime);
      const segmentEnd = timeBucketToDate(seg.endBucket, startTime);

      return [
        {
          startTime: segmentStart,
          endTime: segmentEnd,
          x: position.x,
          y: position.y,
          angle: position.angle,
        },
      ];
    });

    // Only add placements that have at least one successful segment
    if (positionSegments.length > 0) {
      results.push({
        placementId,
        segments: positionSegments,
      });
    } else {
      console.warn(
        `[convertPlacementToPositionSegments] All segments failed for placement ${placementId}, skipping`
      );
    }
  }

  console.log(
    `[convertPlacementToPositionSegments] Converted ${results.length} placements to position segments`
  );

  return results;
}

/**
 * Options for last-minute insertion on ramp
 * Extends temporal parking options with insertion-specific config
 */
export interface LastMinuteInsertionRampOptions {
  /** Planning window start time (default: now) */
  startTime?: Date;
  /** Planning horizon in hours (default: 24) */
  horizonHours?: number;
  /** Maximum number of aircraft moves allowed (0-5) */
  maxDisruption: number;
  /** Weight for stickiness penalty (default: 10.0) */
  stickinessWeight?: number;
  /** Safety buffer in minutes (default: 15) */
  bufferMinutes?: number;
  /** Weight for egress penalty (default: 5.0) */
  egressWeight?: number;
  /** Allow trapping other aircraft (default: false) */
  allowTrapping?: boolean;
  /** Enable local repair tier (default: true) */
  tryLocalRepair?: boolean;
}

/**
 * Result from last-minute insertion operation
 * Extends TemporalPlacementResult with insertion analysis
 */
export interface LastMinuteInsertionRampResult extends TemporalPlacementResult {
  /** Last-minute insertion analysis */
  insertionAnalysis: InsertionResult;
}

/**
 * Run last-minute insertion on ramp
 * Attempts to insert a new trip into existing schedule with minimal disruption
 *
 * Steps:
 * 1. Build spots and zones from ramp geometry
 * 2. Filter existing tenants with positions
 * 3. Build placement map + mark spots occupied (single pass, per-tenant time windows)
 * 4. Validate new trip time range
 * 5. Build ParkingRequest for new trip
 * 6. Call attemptLastMinuteInsertion (three-tier strategy)
 * 7. Apply successful placement back to stack
 * 8. Return with insertion analysis
 *
 * @param ramp Ramp with spot shapes
 * @param stack Current stack with existing aircraft
 * @param newTrip New trip to insert { aircraft, startTime, endTime, trip_id? }
 * @param options Configuration options
 * @returns LastMinuteInsertionRampResult with insertion analysis
 */
export function runLastMinuteInsertionOnRamp(
  ramp: Ramp,
  stack: Stack,
  newTrip: {
    aircraft: any;
    startTime: Date;
    endTime: Date;
    trip_id?: string;
  },
  options: LastMinuteInsertionRampOptions
): LastMinuteInsertionRampResult {
  const startMs = performance.now();

  const {
    startTime = new Date(),
    horizonHours = 24,
    maxDisruption,
    stickinessWeight = 10.0,
    bufferMinutes = 15,
    egressWeight = 5.0,
    allowTrapping = false,
    tryLocalRepair = true,
  } = options;

  // 1. Build spots and zones from ramp geometry
  const spots = buildSpotsFromRamp(ramp, horizonHours);

  if (spots.length === 0) {
    throw new Error("No spots found in ramp. Add shapes with type='Spot'");
  }

  // 2. Filter existing tenants with positions
  const existingTenants = stack.tenants.filter(
    (t) => t.position.x !== null && t.position.y !== null
  );

  // 3. Build placement map and mark spots occupied (single pass)
  // Create spot lookup map for O(1) access
  const spotMap = new Map(spots.map((s) => [s.id, s]));

  const existingPlacements: Placement = {};
  const existingRequests: ParkingRequest[] = [];

  // Filter to only tenants with temporal data (type-safe)
  const scheduledTenants = existingTenants.filter(
    (t): t is ScheduledTenant => {
      // Type guard: check if tenant has required temporal properties
      const hasTemporalData =
        t.trip_id != null &&
        (t as any).start_time != null &&
        (t as any).end_time != null;

      if (!hasTemporalData) {
        console.warn(
          `[runLastMinuteInsertionOnRamp] Tenant ${t.id} missing temporal data, skipping`
        );
      }

      return hasTemporalData;
    }
  );

  for (const tenant of scheduledTenants) {
    // Find which spot tenant is in (using proper point-in-polygon)
    const spotId = findSpotForPosition(
      tenant.position,
      ramp.shapes,
      ramp.depth
    );

    if (!spotId) {
      // Tenant position not in any spot - warn and skip
      console.warn(
        `[runLastMinuteInsertionOnRamp] Tenant ${tenant.id} (${tenant.tail_number}) at position (${tenant.position.x}, ${tenant.position.y}) is not in any spot`
      );
      continue;
    }

    const spot = spotMap.get(spotId);
    if (!spot) {
      console.warn(
        `[runLastMinuteInsertionOnRamp] Spot ${spotId} found for tenant but not in spots array`
      );
      continue;
    }

    // Convert tenant's start/end times to buckets
    const arrivalBucket = timeToBucket(new Date(tenant.start_time), startTime);
    const departureBucket = timeToBucket(new Date(tenant.end_time), startTime);

    // Mark spot as occupied for tenant's time window
    markOccupied(spot, arrivalBucket, departureBucket);

    // Determine orientation from tenant's current angle or use default
    const orientationId = determineOrientation(tenant.position.angle, spot);

    // Build placement entry
    existingPlacements[tenant.id] = {
      spotId,
      orientationId,
    };

    // Build parking request (avoiding duplicate loop)
    existingRequests.push({
      id: tenant.id,
      tail_number: tenant.tail_number,
      sizeCategory: aircraftToSizeCategory(tenant.aircraft),
      arrivalBucket,
      departureBucket,
      aircraft: tenant.aircraft,
    });
  }

  // 4. Validate new trip time range
  if (newTrip.startTime >= newTrip.endTime) {
    throw new Error(
      `Invalid time range: startTime (${newTrip.startTime.toISOString()}) must be before endTime (${newTrip.endTime.toISOString()})`
    );
  }

  // 5. Build ParkingRequest for new trip
  const newRequest: ParkingRequest = {
    id: newTrip.trip_id || `new-${Date.now()}`,
    tail_number: newTrip.aircraft.tail_number,
    sizeCategory: aircraftToSizeCategory(newTrip.aircraft),
    arrivalBucket: timeToBucket(newTrip.startTime, startTime),
    departureBucket: timeToBucket(newTrip.endTime, startTime),
    aircraft: newTrip.aircraft,
    trip_id: newTrip.trip_id,
  };

  // 6. Call attemptLastMinuteInsertion
  const insertionResult = attemptLastMinuteInsertion(
    newRequest,
    existingPlacements,
    spots,
    existingRequests,
    {
      maxDisruption,
      stickinessWeight,
      bufferMinutes,
      egressWeight,
      allowTrapping,
      horizonHours,
      tryLocalRepair,
    }
  );

  // 7. Apply results to stack (if successful)
  let updatedStack = stack;
  if (insertionResult.success && insertionResult.placement) {
    // Apply only the new trip's placement to stack
    const newTripPlacement = insertionResult.placement[newRequest.id];
    if (newTripPlacement && "spotId" in newTripPlacement) {
      // Find spot shape
      const spot = spots.find((s) => s.id === newTripPlacement.spotId);
      if (spot) {
        try {
          // Create minimal tenant object for getPositionForSpot
          const tempTenant: Partial<Tenant> = {
            id: newRequest.id,
            aircraft: newTrip.aircraft,
            tail_number: newRequest.tail_number,
          };

          // Get position for spot with correct arguments
          const position = getPositionForSpot(
            spot,
            ramp.shapes,
            newTripPlacement.orientationId as OrientationType,
            tempTenant as Tenant,
            {}
          );

          if (position) {
            // Create updated stack with new tenant
            // Note: This is a simplified version - real implementation would
            // add tenant to database and reload
            const newTenant: Partial<ScheduledTenant> = {
              id: newRequest.id,
              tail_number: newRequest.tail_number,
              aircraft: newTrip.aircraft,
              position: { x: position.x, y: position.y, angle: position.angle } as any,
              start_time: newTrip.startTime.toISOString(),
              end_time: newTrip.endTime.toISOString(),
            };

            updatedStack = {
              ...stack,
              tenants: [...stack.tenants, newTenant as Tenant],
            };
          }
        } catch (error) {
          // getPositionForSpot may fail if shape doesn't have proper metadata
          // This is okay for algorithm testing - stack update is Phase 6 concern
          console.warn(
            "[runLastMinuteInsertionOnRamp] Could not get position for spot, skipping stack update:",
            error
          );
        }
      }
    }
  }

  const elapsedMs = performance.now() - startMs;

  // 8. Return result with insertion analysis
  return {
    placement: insertionResult.placement || {},
    decisionLog: insertionResult.decisionLog,
    updatedStack,
    statistics: {
      totalAircraft: 1,
      placedAircraft: insertionResult.success ? 1 : 0,
      unplacedAircraft: insertionResult.success ? 0 : 1,
      averageMoves: insertionResult.totalMoves,
      elapsedMs,
    },
    insertionAnalysis: insertionResult,
  };
}

/**
 * Helper: Determine orientation ID from tenant angle
 * Maps tenant's current angle to spot's available orientations
 */
function determineOrientation(tenantAngle: number | null, spot: Spot): string {
  // If tenant has no angle or spot has no orientations, use default
  if (
    tenantAngle === null ||
    !spot.orientations ||
    spot.orientations.length === 0
  ) {
    return "nose_in";
  }

  // For now, just use spot's first orientation
  // Future: Could map angle to closest orientation
  return spot.orientations[0].id;
}

/**
 * Helper: Convert JavaScript Date to bucket index
 */
function timeToBucket(time: Date, startTime: Date): number {
  const diffMs = time.getTime() - startTime.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  const bucket = Math.floor(diffMinutes / 15); // 15-minute buckets
  return Math.max(0, bucket);
}
